/* eslint-disable no-plusplus, no-param-reassign, no-use-before-define, @typescript-eslint/no-use-before-define */
// Hero card: "A mesma partida. Dois caminhos." ONE state feeds the chart, the three rows of numbers, the
// delta chip and all six bars, so no figure can contradict another. On entry the bars grow, then the
// green line and number fall from the plain route's 208 ms to 2–5 ms and keep living there. It runs only
// while the card is on screen and the tab is visible; each time it comes back into view the entrance
// replays. The markup ships the settled state, so reduced motion (and a paused card) show a real chart.

export interface SimHandle {
  setPaused: (paused: boolean) => void;
  destroy: () => void;
}

const N = 40;
const W = 600;
const H = 160;
const Y_TOP = 18;
const Y_BOT = 142;
const CEIL = 260;
const DX = W / N;
const STEP = 460; /* ms per chart sample (the chart scrolls continuously) */
const MINW = 3.5; /* the green bars never vanish */
const GROW = 700;
const DROP_AT = 650;
const DROP = 1500;

export const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
export const rnd = (low: number, high: number) => low + Math.random() * (high - low);
export const rint = (low: number, high: number) => Math.floor(rnd(low, high + 1));
export const expoOut = (t: number) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t));
export function inOut(t: number) {
  const x = clamp(t, 0, 1);
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}
/** frame-rate independent approach to a target */
export const approach = (value: number, target: number, dt: number, tau: number) => value + (target - value) * (1 - Math.exp(-dt / tau));

export function initHeroSim(card: HTMLElement, options: { paused: boolean; decimal: (value: number) => string }): SimHandle {
  const one = <T extends Element>(selector: string) => card.querySelector<T>(selector);
  const offEl = one<HTMLElement>('[data-hsim-off]');
  const onEl = one<HTMLElement>('[data-hsim-on]');
  const chipEl = one<HTMLElement>('[data-hsim-chip]');
  const lossEl = one<HTMLElement>('[data-hsim-loss]');
  const jitEl = one<HTMLElement>('[data-hsim-jit]');
  const badLine = one<SVGPathElement>('[data-hsim-badline]');
  const badArea = one<SVGPathElement>('[data-hsim-badarea]');
  const goodLine = one<SVGPathElement>('[data-hsim-goodline]');
  const goodArea = one<SVGPathElement>('[data-hsim-goodarea]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const bars: Record<string, { el: HTMLElement; v: number }> = {};
  card.querySelectorAll<HTMLElement>('[data-hsim-bar]').forEach((el) => {
    bars[el.getAttribute('data-hsim-bar')!] = { el, v: parseFloat(getComputedStyle(el).getPropertyValue('--w')) || 0 };
  });

  const s = { sem: 208, semT: 208, com: 4, comT: 4, loss: 5.5, lossT: 5.5, jit: 30, jitT: 30, lossG: 0, jitG: 0, bad: [] as number[], low: [] as number[], off: 0, t: 0, phase: 'live' as 'live' | 'entry', grow: 1, nextCom: 0, nextSem: 0 };
  const shown: Record<string, string> = {};

  function badSample() {
    let v = s.semT + (Math.random() - 0.45) * 18;
    if (Math.random() < 0.13) v += 18 + Math.random() * 30;
    return Math.max(186, v);
  }
  const lowSample = () => clamp(s.comT + rnd(-0.7, 0.7), 1.6, 5.4);
  function seed() {
    s.bad = [];
    s.low = [];
    for (let i = 0; i <= N + 1; i++) {
      s.bad.push(badSample());
      s.low.push(lowSample());
    }
    s.off = 0;
  }
  const y = (v: number) => (Y_BOT - (Math.min(v, CEIL) / CEIL) * (Y_BOT - Y_TOP)).toFixed(1);
  /* the drop sweeps from the newest sample (right) back through the history */
  const mixAt = (i: number, p: number) => inOut(clamp(p * 1.5 - (1 - i / N) * 0.5, 0, 1));
  function paint(p: number) {
    if (!badLine || !goodLine) return;
    let pb = '';
    let pg = '';
    for (let i = 0; i <= N + 1; i++) {
      const x = (i * DX - s.off).toFixed(1);
      let g = s.low[i]!;
      if (p < 1) g = s.bad[i]! + (s.low[i]! - s.bad[i]!) * mixAt(i, p);
      pb += `${i ? ' ' : ''}${x} ${y(s.bad[i]!)}`;
      pg += `${i ? ' ' : ''}${x} ${y(g)}`;
    }
    badLine.setAttribute('d', `M${pb}`);
    goodLine.setAttribute('d', `M${pg}`);
    badArea?.setAttribute('d', `M${pb} L${W} ${H} L0 ${H} Z`);
    goodArea?.setAttribute('d', `M${pg} L${W} ${H} L0 ${H} Z`);
  }
  function setText(key: string, el: HTMLElement | null, txt: string) {
    if (el && shown[key] !== txt) {
      el.textContent = txt;
      shown[key] = txt;
    }
  }
  function setBar(key: string, target: number, dt: number | null, tau: number) {
    const bar = bars[key];
    if (!bar) return;
    bar.v = dt == null ? target : approach(bar.v, target, dt, tau);
    bar.el.style.setProperty('--w', `${bar.v.toFixed(2)}%`);
  }
  function render(dt: number | null) {
    const semR = Math.round(s.sem);
    const comR = Math.round(s.com);
    const chip = Math.max(0, Math.round((1 - comR / semR) * 100));
    setText('off', offEl, String(semR));
    setText('on', onEl, String(comR));
    setText('chip', chipEl, `−${chip}%`);
    setText('loss', lossEl, options.decimal(Math.round(s.loss * 10) / 10));
    setText('jit', jitEl, String(Math.round(s.jit)));
    const g = s.grow;
    setBar('ping-bad', g * clamp((semR / 240) * 100, 0, 100), dt, 150);
    /* green: the Ping bar tracks its 2–5 ms figure inside a visible floor (3.0–4.8%) */
    setBar('ping-good', g * Math.max(3 + (clamp(comR, 2, 5) - 2) * 0.6, (comR / 240) * 100), dt, 260);
    setBar('loss-bad', g * clamp((s.loss / 8) * 100, 0, 100), dt, 150);
    setBar('loss-good', g * (MINW + s.lossG), dt, 260);
    setBar('jit-bad', g * clamp((Math.round(s.jit) / 40) * 100, 0, 100), dt, 150);
    setBar('jit-good', g * (5 + s.jitG), dt, 260);
  }
  function live(now: number, dt: number) {
    if (now >= s.nextCom) {
      let c;
      do c = rint(2, 5);
      while (c === s.comT);
      s.comT = c;
      s.nextCom = now + rnd(800, 1500);
    }
    if (now >= s.nextSem) {
      s.semT = clamp(s.semT + rint(-9, 9), 200, 224);
      s.lossT = clamp(Math.round((s.lossT + rnd(-0.5, 0.5)) * 10) / 10, 4.6, 6.4);
      s.jitT = rint(25, 35);
      s.lossG = rnd(-0.4, 0.4);
      s.jitG = rnd(-0.4, 0.4);
      s.nextSem = now + rnd(420, 760);
    }
    s.com = approach(s.com, s.comT, dt, 170);
    s.sem = approach(s.sem, s.semT, dt, 200);
    s.loss = approach(s.loss, s.lossT, dt, 200);
    s.jit = approach(s.jit, s.jitT, dt, 160);
  }

  let raf = 0;
  let last = 0;
  let visible = false;
  let needEntry = true;
  let { paused } = options;
  function frame(now: number) {
    raf = 0;
    const dt = Math.min(64, now - (last || now));
    last = now;
    s.t += dt;
    s.off += (dt / STEP) * DX;
    while (s.off >= DX) {
      s.off -= DX;
      s.bad.shift();
      s.bad.push(badSample());
      s.low.shift();
      s.low.push(lowSample());
    }
    let p = 1;
    if (s.phase === 'entry') {
      s.grow = expoOut(s.t / GROW);
      p = clamp((s.t - DROP_AT) / DROP, 0, 1);
      s.sem = approach(s.sem, s.semT, dt, 200);
      s.com = s.sem + (s.comT - s.sem) * mixAt(N, p);
      if (p >= 1 && s.t >= GROW) {
        s.phase = 'live';
        s.grow = 1;
        s.nextCom = now + rnd(700, 1200);
        s.nextSem = now + 400;
      }
      render(null);
    } else {
      live(now, dt);
      render(dt);
    }
    paint(p);
    schedule();
  }
  function schedule() {
    if (!raf && visible && !paused && !document.hidden) raf = requestAnimationFrame(frame);
  }
  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    last = 0;
  }
  function startEntry() {
    seed();
    s.semT = 208;
    s.sem = 208;
    s.com = 208;
    s.comT = rint(3, 4);
    s.loss = 5.5;
    s.lossT = 5.5;
    s.jit = 30;
    s.jitT = 30;
    s.lossG = 0;
    s.jitG = 0;
    s.t = 0;
    s.grow = 0;
    s.phase = 'entry';
    Object.values(bars).forEach((bar) => {
      bar.el.style.setProperty('--w', '0%');
      bar.v = 0;
    });
    render(null);
    paint(0);
    needEntry = false;
    last = 0;
    schedule();
  }

  card.classList.toggle('is-paused', paused);
  /* settled first paint (static state, a real chart) until the entrance runs */
  seed();
  paint(1);

  const onVisibility = () => {
    if (document.hidden) stop();
    else schedule();
  };
  let observer: IntersectionObserver | null = null;
  if (typeof IntersectionObserver !== 'function') {
    visible = true;
    if (!paused) startEntry();
  } else {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !visible) {
            visible = true;
            if (!paused) startEntry();
            else needEntry = !reduced.matches;
          } else if (!entry.isIntersecting && visible) {
            visible = false;
            stop();
            needEntry = true;
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(card);
    document.addEventListener('visibilitychange', onVisibility);
  }

  return {
    setPaused(next) {
      if (next === paused) return;
      paused = next;
      card.classList.toggle('is-paused', paused);
      if (paused) {
        stop();
        return;
      }
      if (needEntry && !reduced.matches) startEntry();
      else {
        needEntry = false;
        schedule();
      }
    },
    destroy() {
      stop();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
